import { useEffect, useMemo, useRef, useState } from "react";
import { api, money, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import BulkActionBar from "@/components/BulkActionBar";
import useBulkSelection from "@/hooks/useBulkSelection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Package, Filter, X, CheckCircle2, Ban, Tag, FileDown } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import VariantManager from "@/components/VariantManager";
import { exportProductsToXLSX } from "@/lib/exportProducts";

/**
 * EditableNumCell — inline-editable numeric cell used for MRP / Selling
 * Price / Stock quick-edit directly from the Products list. Enter or blur
 * commits, Esc reverts. Prevents row-click from bubbling through.
 */
function EditableNumCell({ value, onSave, prefix = "", "data-testid": testid, min = 0, step = "any", disabled = false }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  const inputRef = useRef(null);

  useEffect(() => { if (!editing) setVal(value ?? ""); }, [value, editing]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = async () => {
    const num = val === "" ? null : Number(val);
    if (num !== null && Number.isNaN(num)) { toast.error("Enter a valid number"); return; }
    if (num !== null && num < min) { toast.error(`Value cannot be less than ${min}`); return; }
    setEditing(false);
    if (num !== (value ?? null)) {
      try { await onSave(num); } catch (e) { toast.error(errMsg(e)); }
    }
  };

  if (!editing) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); if (!disabled) setEditing(true); }}
        disabled={disabled}
        className={`w-full text-left px-2 py-1 rounded-md border border-transparent transition text-sm font-semibold tabular-nums ${disabled ? "opacity-60 cursor-not-allowed" : "hover:bg-secondary/50 hover:border-border"}`}
        data-testid={testid}
      >
        {prefix}{value ?? "—"}
      </button>
    );
  }
  return (
    <Input
      ref={inputRef}
      type="number"
      step={step}
      min={min}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") { setEditing(false); setVal(value ?? ""); }
      }}
      onBlur={commit}
      onClick={(e) => e.stopPropagation()}
      className="h-8 w-24 text-sm tabular-nums"
      data-testid={`${testid}-input`}
    />
  );
}


const emptyForm = {
  name: "", sku: "", barcode: "", description: "",
  mrp: "", selling_price: "", cost_price: "",
  gst_rate: 5, hsn_code: "", tax_inclusive: false,
  unit: "piece", weight: "", weight_unit: "g",
  thumbnail_url: "", brand_id: "", category_id: "", subcategory_id: "",
  track_stock: true, min_stock: 0, reorder_level: 0,
  // Sprint 15 — initial stock (delta) + MOQ + show_stock toggle
  // Sprint 59 — max_order_qty per-order customer cap
  initial_stock: 0, quantity: 0, moq: 1, max_order_qty: "", show_stock: true,
  featured: false, trending: false, new_arrival: false, hot_deal: false, best_seller: false,
  is_active: true,
  is_online: true,     // Sprint — Online Visibility (customer app / website)
};

export default function Products() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentStock, setCurrentStock] = useState(null);
  // Sprint 73 — Inline edit: per-product base stock map keyed by product_id.
  const [stockMap, setStockMap] = useState({});
  const [inlineBusy, setInlineBusy] = useState({});

  // Advanced filter state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    category_id: "",
    brand_id: "",
    status: "",         // "" | "active" | "inactive"
    featured: "",       // "" | "yes" | "no"
    price_min: "",
    price_max: "",
  });

  const sel = useBulkSelection();

  const filterCount = useMemo(() => (
    Object.entries(filters).filter(([, v]) => v !== "" && v !== null).length
  ), [filters]);

  const fetchRows = async (pg = page, query = q, f = filters) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: pg, limit });
      if (query) qs.set("q", query);
      if (f.category_id) qs.set("category_id", f.category_id);
      if (f.brand_id) qs.set("brand_id", f.brand_id);
      if (f.status === "active") qs.set("status", "active");
      else if (f.status === "inactive") qs.set("status", "inactive");
      if (f.featured === "yes") qs.set("featured", "true");
      else if (f.featured === "no") qs.set("featured", "false");
      const { data } = await api.get(`/products?${qs}`);
      let items = data.items || [];
      // Client-side price range (backend doesn't support it).
      const pmin = Number(f.price_min || 0);
      const pmax = Number(f.price_max || 0);
      if (pmin > 0) items = items.filter((r) => Number(r.selling_price || 0) >= pmin);
      if (pmax > 0) items = items.filter((r) => Number(r.selling_price || 0) <= pmax);
      setRows(items);
      setTotal(data.total || 0);
      // Sprint 73 — hydrate base-stock map so the Stock column can inline-edit.
      // Uses one lightweight /inventory call scoped to this store.
      try {
        const inv = await api.get(`/inventory?limit=200`);
        const map = {};
        for (const r of (inv.data.items || [])) {
          if (!r.variant_id && r.product_id) {
            map[r.product_id] = Number(r.qty_on_hand ?? 0);
          }
        }
        setStockMap(map);
      } catch { /* silent — column just shows — */ }
    } catch (e) {
      toast.error(errMsg(e, "Failed to load products"));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchRows(page, q, filters); /* eslint-disable-next-line */ }, [page]);

  useEffect(() => {
    api.get("/categories?limit=100").then((r) => setCategories(r.data.items || [])).catch(() => {});
    api.get("/brands?limit=100").then((r) => setBrands(r.data.items || [])).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setCurrentStock(null);
    setSubmitted(false);
    setOpen(true);
  };
  const openEdit = async (p) => {
    setEditing(p);
    setSubmitted(false);
    setForm({
      ...emptyForm, ...p,
      mrp: p.mrp ?? "", selling_price: p.selling_price ?? "", cost_price: p.cost_price ?? "",
      brand_id: p.brand_id || "", category_id: p.category_id || "", subcategory_id: p.subcategory_id || "",
      thumbnail_url: p.thumbnail_url || "", weight: p.weight ?? "",
      moq: p.moq ?? 1, max_order_qty: p.max_order_qty ?? "", show_stock: p.show_stock !== false,
      is_online: p.is_online !== false,       // Sprint — Online Visibility (missing → true)
      initial_stock: 0,
      quantity: 0,
    });
    setOpen(true);
    // Fetch current on-hand for display
    try {
      const { data } = await api.get(`/inventory?product_id=${p.id}&limit=1`);
      const inv = (data.items || []).find((r) => !r.variant_id) || (data.items || [])[0];
      const stock = inv ? Number(inv.qty_on_hand || 0) : 0;
      setCurrentStock(stock);
      setForm((prev) => ({ ...prev, initial_stock: stock, quantity: stock }));
    } catch { setCurrentStock(null); }
  };

  const save = async () => {
    setSubmitted(true);
    setSaving(true);
    try {
      const stockQty = Number(form.quantity !== undefined && form.quantity !== "" ? form.quantity : form.initial_stock || 0);
      const name = (form.name || "").trim();
      const sellingPrice = Number(form.selling_price || 0);
      const gstRate = form.gst_rate === "" || form.gst_rate === null || form.gst_rate === undefined ? null : Number(form.gst_rate);

      // Client-side required validation with clear error surface
      if (!name) { toast.error("Product name is required"); setSaving(false); return; }
      if (!sellingPrice || sellingPrice <= 0) { toast.error("Selling price must be greater than 0"); setSaving(false); return; }
      if (gstRate === null || isNaN(gstRate) || gstRate < 0) { toast.error("GST % is required (enter 0 or greater)"); setSaving(false); return; }

      const payload = {
        ...form,
        name,
        sku: (form.sku || "").trim() || null,
        barcode: (form.barcode || "").trim() || null,
        hsn_code: (form.hsn_code || "").trim() || null,
        description: (form.description || "").trim(),
        thumbnail_url: (form.thumbnail_url || "").trim() || null,
        mrp: Number(form.mrp || 0),
        selling_price: sellingPrice,
        cost_price: form.cost_price === "" || form.cost_price == null ? null : Number(form.cost_price),
        gst_rate: gstRate,
        min_stock: Number(form.min_stock || 0),
        reorder_level: Number(form.reorder_level || 0),
        moq: Math.max(1, Number(form.moq || 1)),
        max_order_qty: form.max_order_qty === "" || form.max_order_qty == null ? null : Math.max(1, Number(form.max_order_qty)),
        show_stock: !!form.show_stock,
        weight: form.weight === "" || form.weight == null ? null : Number(form.weight),
        brand_id: form.brand_id || null,
        category_id: form.category_id || null,
        subcategory_id: form.subcategory_id || null,
      };

      // Strip UI-only fields so backend doesn't reject
      delete payload.initial_stock;
      delete payload.quantity;

      let saved;
      if (editing) {
        const { data } = await api.patch(`/products/${editing.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/products", payload);
        saved = data;
      }

      // Sync stock via inventory.adjust so the product has the exact stock quantity
      if (saved?.id) {
        const current = editing ? Number(currentStock ?? 0) : 0;
        const delta = stockQty - current;
        if (delta !== 0) {
          try {
            await api.post("/inventory/adjust", {
              product_id: saved.id,
              variant_id: null,
              delta_qty: delta,
              reason: editing ? "manual_edit" : "initial_stock",
            });
          } catch (e) {
            toast.warning("Product saved, but stock adjust failed: " + (errMsg(e) || "unknown"));
          }
        }
      }

      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false);
      fetchRows(page, q, filters);
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      fetchRows(page, q, filters);
    } catch (e) { toast.error(errMsg(e)); }
  };

  // ── Bulk actions ─────────────────────────────────────────────────────
  const runBulk = async (fn, verb) => {
    const ids = sel.ids;
    if (ids.length === 0) return;
    if (verb === "delete" && !window.confirm(`Delete ${ids.length} selected product${ids.length > 1 ? "s" : ""}? This cannot be undone.`)) return;
    const t = toast.loading(`${verb === "delete" ? "Deleting" : verb === "activate" ? "Activating" : "Deactivating"} ${ids.length}…`);
    const results = await Promise.allSettled(ids.map(fn));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = ids.length - ok;
    toast.dismiss(t);
    if (fail === 0) toast.success(`${verb === "delete" ? "Deleted" : verb === "activate" ? "Activated" : "Deactivated"} ${ok}`);
    else toast.warning(`${ok} ok · ${fail} failed`);
    sel.clear();
    fetchRows(page, q, filters);
  };
  const bulkDelete     = () => runBulk((id) => api.delete(`/products/${id}`), "delete");
  const bulkActivate   = () => runBulk((id) => api.patch(`/products/${id}`, { is_active: true }), "activate");
  const bulkDeactivate = () => runBulk((id) => api.patch(`/products/${id}`, { is_active: false }), "deactivate");

  // Bulk homepage tag apply — Featured / Hot Deal / Trending / Best Seller / New Arrival
  const [bulkTagsOpen, setBulkTagsOpen] = useState(false);
  const applyBulkTag = async (key, value) => {
    const ids = sel.ids;
    if (ids.length === 0) return;
    const t = toast.loading(`Updating ${ids.length}…`);
    const results = await Promise.allSettled(ids.map((id) => api.patch(`/products/${id}`, { [key]: value })));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = ids.length - ok;
    toast.dismiss(t);
    if (fail === 0) toast.success(`${key.replace("_", " ")} ${value ? "ON" : "OFF"} for ${ok}`);
    else toast.warning(`${ok} ok · ${fail} failed`);
    sel.clear();
    setBulkTagsOpen(false);
    fetchRows(page, q, filters);
  };
  const TAG_OPTIONS = [
    { key: "featured",    label: "Featured" },
    { key: "hot_deal",    label: "Hot Deal" },
    { key: "trending",    label: "Trending" },
    { key: "best_seller", label: "Best Seller" },
    { key: "new_arrival", label: "New Arrival" },
  ];

  // Sprint 73 — Inline edit handlers. Optimistic UI + toast on failure.
  const patchProductField = async (id, field, value) => {
    setInlineBusy((b) => ({ ...b, [id]: true }));
    const prev = rows.find((r) => r.id === id);
    setRows((rs) => rs.map((r) => r.id === id ? { ...r, [field]: value } : r));
    try {
      await api.patch(`/products/${id}`, { [field]: value });
      toast.success("Saved");
    } catch (e) {
      // Revert on failure
      setRows((rs) => rs.map((r) => r.id === id ? { ...r, [field]: prev?.[field] ?? null } : r));
      throw e;
    } finally {
      setInlineBusy((b) => { const n = { ...b }; delete n[id]; return n; });
    }
  };

  const setStockAbsolute = async (id, target) => {
    const t = Math.max(0, Math.round(Number(target ?? 0)));
    const current = Number(stockMap[id] ?? 0);
    const delta = t - current;
    if (delta === 0) return;
    setInlineBusy((b) => ({ ...b, [`stock-${id}`]: true }));
    // Optimistic
    setStockMap((m) => ({ ...m, [id]: t }));
    try {
      await api.post("/inventory/adjust", {
        product_id: id,
        variant_id: null,
        delta_qty: delta,
        reason: "manual_edit",
      });
      toast.success("Stock updated");
    } catch (e) {
      // Revert on failure
      setStockMap((m) => ({ ...m, [id]: current }));
      throw e;
    } finally {
      setInlineBusy((b) => { const n = { ...b }; delete n[`stock-${id}`]; return n; });
    }
  };

  const columns = useMemo(() => [
    {
      key: "name", header: "Product",
      render: (r) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-secondary grid place-items-center overflow-hidden shrink-0">
            {r.thumbnail_url ? <img src={r.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-muted-foreground" />}
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{r.name}</div>
            <div className="text-xs text-muted-foreground truncate">{r.sku || "no SKU"}{r.barcode ? ` · ${r.barcode}` : ""}</div>
          </div>
        </div>
      ),
    },
    // Sprint 73 — Direct Edit: MRP · Selling Price · Stock inline cells.
    { key: "mrp", header: "MRP", className: "w-28",
      render: (r) => (
        <EditableNumCell
          value={r.mrp}
          prefix="₹"
          onSave={(v) => patchProductField(r.id, "mrp", v)}
          data-testid={`product-mrp-${r.id}`}
        />
      ),
    },
    { key: "selling_price", header: "Sell Price", className: "w-28",
      render: (r) => (
        <EditableNumCell
          value={r.selling_price}
          prefix="₹"
          onSave={(v) => patchProductField(r.id, "selling_price", v)}
          data-testid={`product-sp-${r.id}`}
        />
      ),
    },
    { key: "__stock", header: "Stock", className: "w-24",
      render: (r) => (
        <EditableNumCell
          value={stockMap[r.id] ?? 0}
          step="1"
          onSave={(v) => setStockAbsolute(r.id, v)}
          data-testid={`product-stock-${r.id}`}
          disabled={r.has_variants}
        />
      ),
    },
    { key: "gst_rate", header: "GST", render: (r) => `${r.gst_rate ?? 0}%`, className: "w-20" },
    { key: "status", header: "Status",
      render: (r) => <Badge variant={r.is_active ? "default" : "secondary"} className={r.is_active ? "bg-accent hover:bg-accent" : ""}>{r.is_active ? "Active" : "Inactive"}</Badge>,
      className: "w-24",
    },
    // Sprint — Online Visibility. Inline toggle so admins can hide a product
    // from the customer app/website with one tap without opening the edit dialog.
    { key: "is_online", header: "Online", className: "w-24",
      render: (r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Switch
            checked={r.is_online !== false}
            onCheckedChange={async (v) => {
              try {
                await api.patch(`/products/${r.id}`, { is_online: v });
                setRows((rows) => rows.map((x) => x.id === r.id ? { ...x, is_online: v } : x));
                toast.success(v ? "Now visible online" : "Hidden from customer app");
              } catch { toast.error("Could not update visibility"); }
            }}
            data-testid={`product-online-${r.id}`}
          />
        </div>
      ),
    },
    { key: "actions", header: "", className: "w-24 text-right",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(r); }} data-testid={`edit-product-${r.id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); del(r); }} data-testid={`delete-product-${r.id}`}>
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [categories, brands, stockMap]);

  // Sprint — Bulk Product Export (.xlsx). Backend scopes by X-Store-Id header,
  // so SUPER_ADMIN on /platform gets all stores; STORE_ADMIN gets only their store.
  const [exporting, setExporting] = useState(false);
  const runExport = async () => {
    if (exporting) return;
    setExporting(true);
    const t = toast.loading("Preparing Excel export…");
    try {
      const count = await exportProductsToXLSX();
      toast.dismiss(t);
      toast.success(`Exported ${count} product${count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.dismiss(t);
      toast.error(errMsg(e, "Export failed"));
    } finally { setExporting(false); }
  };

  return (
    <div className="p-4 md:p-8" data-testid="products-page">
      <PageHeader
        title="Products"
        subtitle="Catalog & pricing"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={runExport}
              disabled={exporting}
              data-testid="products-export-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              {exporting ? "Exporting…" : "Export Excel"}
            </Button>
            <Button
              variant={filtersOpen || filterCount > 0 ? "default" : "outline"}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="products-filter-toggle"
              className={filterCount > 0 ? "" : ""}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters{filterCount > 0 ? ` · ${filterCount}` : ""}
            </Button>
            <Button onClick={openCreate} className="btn-primary-glow" data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" />New product
            </Button>
          </div>
        }
      />

      {filtersOpen && (
        <div className="mb-4 rounded-2xl border bg-card p-4 space-y-3" data-testid="products-filter-panel">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-[11px]">Category</Label>
              <Select value={filters.category_id || "__any"} onValueChange={(v) => setFilters((f) => ({ ...f, category_id: v === "__any" ? "" : v }))}>
                <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Any</SelectItem>
                  {categories.filter((c) => !c.parent_id && !/^TEST[_-]/i.test(c.name || "")).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Brand</Label>
              <Select value={filters.brand_id || "__any"} onValueChange={(v) => setFilters((f) => ({ ...f, brand_id: v === "__any" ? "" : v }))}>
                <SelectTrigger data-testid="filter-brand"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Any</SelectItem>
                  {brands.filter((b) => !/^TEST[_-]/i.test(b.name || "")).map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Status</Label>
              <Select value={filters.status || "__any"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "__any" ? "" : v }))}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Any</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Featured</Label>
              <Select value={filters.featured || "__any"} onValueChange={(v) => setFilters((f) => ({ ...f, featured: v === "__any" ? "" : v }))}>
                <SelectTrigger data-testid="filter-featured"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Any</SelectItem>
                  <SelectItem value="yes">Featured only</SelectItem>
                  <SelectItem value="no">Not featured</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Min price</Label>
              <Input type="number" placeholder="₹0" value={filters.price_min} onChange={(e) => setFilters((f) => ({ ...f, price_min: e.target.value }))} data-testid="filter-price-min" />
            </div>
            <div>
              <Label className="text-[11px]">Max price</Label>
              <Input type="number" placeholder="₹∞" value={filters.price_max} onChange={(e) => setFilters((f) => ({ ...f, price_max: e.target.value }))} data-testid="filter-price-max" />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" onClick={() => { setPage(1); fetchRows(1, q, filters); }} className="btn-primary-glow" data-testid="filter-apply">
              Apply filters
            </Button>
            <Button size="sm" variant="ghost" onClick={() => {
              const clean = { category_id: "", brand_id: "", status: "", featured: "", price_min: "", price_max: "" };
              setFilters(clean); setPage(1); fetchRows(1, q, clean);
            }} data-testid="filter-clear">
              <X className="w-3.5 h-3.5 mr-1" />Clear all
            </Button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns} rows={rows} loading={loading}
        onSearch={(v) => { setQ(v); setPage(1); fetchRows(1, v, filters); }}
        searchPlaceholder="Search by name, SKU, barcode…"
        page={page} limit={limit} total={total} onPage={setPage}
        onRowClick={openEdit}
        selection={sel}
        testId="products-table"
      />

      <BulkActionBar
        count={sel.count}
        onClear={sel.clear}
        actions={[
          { label: "Bulk Actions", icon: <Tag className="w-3.5 h-3.5" />,        onClick: () => setBulkTagsOpen(true), testId: "bulk-tags" },
          { label: "Activate",   icon: <CheckCircle2 className="w-3.5 h-3.5" />, onClick: bulkActivate,   testId: "bulk-activate" },
          { label: "Deactivate", icon: <Ban className="w-3.5 h-3.5" />,          onClick: bulkDeactivate, testId: "bulk-deactivate" },
          { label: "Delete",     icon: <Trash2 className="w-3.5 h-3.5" />,       onClick: bulkDelete, tone: "destructive", testId: "bulk-delete" },
        ]}
      />

      {/* Bulk homepage-tag apply dialog */}
      <Dialog open={bulkTagsOpen} onOpenChange={setBulkTagsOpen}>
        <DialogContent className="max-w-md w-[calc(100vw-1rem)] p-0" data-testid="bulk-tags-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b">
            <DialogTitle className="font-display text-xl">Bulk Actions</DialogTitle>
            <p className="text-xs text-muted-foreground">Apply a homepage tag to <span className="font-semibold text-foreground">{sel.count}</span> selected product{sel.count === 1 ? "" : "s"}.</p>
          </DialogHeader>
          <div className="px-5 py-4 space-y-2">
            {TAG_OPTIONS.map((t) => (
              <div key={t.key} className="flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5" data-testid={`bulk-tag-row-${t.key}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{t.label}</div>
                  <div className="text-[10px] text-muted-foreground leading-tight">Toggle for all selected</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="h-8 px-3" onClick={() => applyBulkTag(t.key, true)} data-testid={`bulk-tag-${t.key}-on`}>ON</Button>
                  <Button size="sm" variant="ghost"   className="h-8 px-3" onClick={() => applyBulkTag(t.key, false)} data-testid={`bulk-tag-${t.key}-off`}>OFF</Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="px-5 py-3 border-t bg-secondary/30">
            <Button variant="outline" onClick={() => setBulkTagsOpen(false)} className="w-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-3xl w-[calc(100vw-2rem)] max-h-[92vh] flex flex-col p-0 overflow-hidden rounded-2xl shadow-2xl border-border/80"
          data-testid="product-dialog"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="px-5 py-3 border-b bg-card shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="font-display text-xl font-bold tracking-tight">
                  {editing ? "Edit Product" : "New Product"}
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Fields marked with <span className="text-destructive font-semibold">*</span> (Name, Selling price, GST %) are required.
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="px-5 py-3 overflow-y-auto flex-1 space-y-3">
            {/* ── 1 · BASIC INFORMATION ───────────────────────────────── */}
            <section className="rounded-xl border border-border/70 bg-card/60 p-3 space-y-2.5 shadow-sm">
              <div className="flex items-center gap-2 pb-1.5 border-b border-border/50">
                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center">1</span>
                <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">Basic Information</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                <div className="col-span-1 sm:col-span-2 md:col-span-3">
                  <Label className="text-xs font-semibold text-foreground/85">Product Name <span className="text-destructive">*</span></Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="product-name"
                    placeholder="e.g. Aashirvaad Superior MP Whole Wheat Atta 5kg"
                    aria-required="true"
                    className={`mt-1 h-9 ${submitted && !form.name?.trim() ? "border-destructive focus-visible:ring-destructive/30" : ""}`}
                  />
                  {submitted && !form.name?.trim() && <p className="text-[11px] text-destructive mt-0.5 font-medium">Product name is required</p>}
                </div>

                <div>
                  <Label className="text-xs font-semibold text-foreground/85">Parent Category</Label>
                  <Select value={form.category_id || "__none"} onValueChange={(v) => setForm({ ...form, category_id: v === "__none" ? "" : v, subcategory_id: "" })}>
                    <SelectTrigger className="mt-1 h-9 w-full" data-testid="product-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">— None —</SelectItem>
                      {categories.filter((c) => !c.parent_id && !/^TEST[_-]/i.test(c.name || "")).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-foreground/85">Sub Category</Label>
                  {(() => {
                    const subs = categories.filter((c) => c.parent_id === form.category_id && !/^TEST[_-]/i.test(c.name || ""));
                    return (
                      <Select value={form.subcategory_id || "__none"} onValueChange={(v) => setForm({ ...form, subcategory_id: v === "__none" ? "" : v })} disabled={!form.category_id || subs.length === 0}>
                        <SelectTrigger className="mt-1 h-9 w-full" data-testid="product-subcategory"><SelectValue placeholder={!form.category_id ? "Pick parent first" : subs.length === 0 ? "No sub-categories" : "Select sub-category"} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">— None —</SelectItem>
                          {subs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </div>

                <div>
                  <Label className="text-xs font-semibold text-foreground/85">Brand</Label>
                  <Select value={form.brand_id || "__none"} onValueChange={(v) => setForm({ ...form, brand_id: v === "__none" ? "" : v })}>
                    <SelectTrigger className="mt-1 h-9 w-full" data-testid="product-brand"><SelectValue placeholder="Select brand" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">— None —</SelectItem>
                      {brands.filter((b) => !/^TEST[_-]/i.test(b.name || "")).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-1 sm:col-span-1 md:col-span-1.5">
                  <Label className="text-xs font-semibold text-foreground/85">SKU (Item Code)</Label>
                  <Input value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-sku" placeholder="e.g. AASH-ATTA-5KG" className="mt-1 h-9" />
                </div>

                <div className="col-span-1 sm:col-span-1 md:col-span-1.5">
                  <Label className="text-xs font-semibold text-foreground/85">Barcode (EAN / UPC)</Label>
                  <Input value={form.barcode || ""} onChange={(e) => setForm({ ...form, barcode: e.target.value })} data-testid="product-barcode" placeholder="e.g. 8901030383748" className="mt-1 h-9" />
                </div>
              </div>
            </section>

            {/* ── 2 · PRICING & TAXES ─────────────────────────────────── */}
            <section className="rounded-xl border border-border/70 bg-card/60 p-3 space-y-2.5 shadow-sm">
              <div className="flex items-center gap-2 pb-1.5 border-b border-border/50">
                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center">2</span>
                <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">Pricing & Taxes</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                <div>
                  <Label className="text-xs font-semibold text-foreground/85">Selling Price (₹) <span className="text-destructive">*</span></Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-semibold">₹</span>
                    <Input
                      type="number" step="0.01" min="0"
                      value={form.selling_price}
                      onChange={(e) => setForm({ ...form, selling_price: e.target.value })}
                      data-testid="product-price" placeholder="0.00"
                      aria-required="true"
                      className={`pl-7 h-9 font-medium ${submitted && (!form.selling_price || Number(form.selling_price) <= 0) ? "border-destructive focus-visible:ring-destructive/30" : ""}`}
                    />
                  </div>
                  {submitted && (!form.selling_price || Number(form.selling_price) <= 0) && <p className="text-[11px] text-destructive mt-0.5 font-medium">Must be greater than 0</p>}
                </div>

                <div>
                  <Label className="text-xs font-semibold text-foreground/85">MRP (₹)</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-semibold">₹</span>
                    <Input type="number" step="0.01" min="0" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} data-testid="product-mrp" placeholder="0.00" className="pl-7 h-9 font-medium" />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Printed retail price</p>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-foreground/85">Cost Price (₹)</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-semibold">₹</span>
                    <Input type="number" step="0.01" min="0" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} placeholder="0.00" className="pl-7 h-9 font-medium" />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Purchase / inventory cost</p>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-foreground/85">GST Rate (%) <span className="text-destructive">*</span></Label>
                  <div className="relative mt-1">
                    <Input
                      type="number" step="0.1" min="0" max="100"
                      value={form.gst_rate}
                      onChange={(e) => setForm({ ...form, gst_rate: e.target.value })}
                      data-testid="product-gst"
                      placeholder="e.g. 5"
                      className={`h-9 pr-8 font-medium ${submitted && (form.gst_rate === "" || form.gst_rate === null || Number(form.gst_rate) < 0) ? "border-destructive focus-visible:ring-destructive/30" : ""}`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-semibold">%</span>
                  </div>
                  {submitted && (form.gst_rate === "" || form.gst_rate === null || Number(form.gst_rate) < 0) ? (
                    <p className="text-[11px] text-destructive mt-0.5 font-medium">GST % is required</p>
                  ) : (
                    <div className="flex gap-1 mt-1">
                      {[0, 5, 12, 18, 28].map((rate) => (
                        <button
                          type="button"
                          key={rate}
                          onClick={() => setForm({ ...form, gst_rate: rate })}
                          className={`text-[10px] px-1.5 py-0.5 rounded border transition ${
                            Number(form.gst_rate) === rate ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"
                          }`}
                        >
                          {rate}%
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-xs font-semibold text-foreground/85">HSN Code</Label>
                  <Input value={form.hsn_code || ""} onChange={(e) => setForm({ ...form, hsn_code: e.target.value })} placeholder="e.g. 1101" className="mt-1 h-9" />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Tax classification code</p>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-foreground/85">Tax Inclusive</Label>
                  <div className="flex items-center justify-between rounded-lg border border-border/80 bg-muted/20 px-3 mt-1 h-9">
                    <span className="text-xs font-semibold text-foreground">Tax inclusive</span>
                    <Switch checked={!!form.tax_inclusive} onCheckedChange={(v) => setForm({ ...form, tax_inclusive: v })} data-testid="product-tax-inclusive" />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Selling price already includes GST</p>
                </div>
              </div>
            </section>

            {/* ── 3 · INVENTORY & STOCK ───────────────────────────────── */}
            <section className="rounded-xl border border-border/70 bg-card/60 p-3 space-y-2.5 shadow-sm">
              <div className="flex items-center gap-2 pb-1.5 border-b border-border/50">
                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center">3</span>
                <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">Inventory & Stock</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                <div className="col-span-1 sm:col-span-2 md:col-span-1">
                  <Label className="text-xs font-semibold text-foreground/85">
                    Quantity (Available Stock)
                  </Label>
                  <Input
                    type="number" min="0" step="1"
                    value={form.quantity !== undefined ? form.quantity : form.initial_stock}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value, initial_stock: e.target.value })}
                    data-testid="product-quantity"
                    placeholder="e.g. 100"
                    className="mt-1 h-9 font-medium"
                  />
                  {editing && currentStock !== null ? (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Current on-hand: <span className="font-semibold text-foreground">{currentStock}</span>
                    </p>
                  ) : (
                    <p className="text-[10px] text-muted-foreground mt-0.5">Starting inventory count</p>
                  )}
                </div>

                <div>
                  <Label className="text-xs font-semibold text-foreground/85">Low Stock Alert (Min Stock)</Label>
                  <Input type="number" min="0" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} data-testid="product-min-stock" placeholder="Alert below" className="mt-1 h-9" />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Warning when stock drops below</p>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-foreground/85">Reorder Level</Label>
                  <Input type="number" min="0" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} data-testid="product-reorder" placeholder="Reorder when ≤" className="mt-1 h-9" />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Suggested PO trigger point</p>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-foreground/85">Min Order Qty (MOQ)</Label>
                  <Input type="number" min="1" value={form.moq} onChange={(e) => setForm({ ...form, moq: e.target.value })} data-testid="product-moq" placeholder="Default 1" className="mt-1 h-9" />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Min items customer must buy</p>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-foreground/85">Max Order Qty (Per order)</Label>
                  <Input type="number" min="1" value={form.max_order_qty} onChange={(e) => setForm({ ...form, max_order_qty: e.target.value })} data-testid="product-max-order-qty" placeholder="No cap" className="mt-1 h-9" />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Leave empty for no limit</p>
                </div>

                <div className="flex flex-col gap-1.5 justify-center">
                  <div className="flex items-center justify-between rounded-lg border border-border/80 bg-muted/20 px-3 py-1.5">
                    <div className="min-w-0 pr-2">
                      <div className="text-xs font-medium text-foreground">Track Stock</div>
                      <div className="text-[10px] text-muted-foreground leading-tight">Prevent over-selling</div>
                    </div>
                    <Switch checked={!!form.track_stock} onCheckedChange={(v) => setForm({ ...form, track_stock: v })} data-testid="product-track-stock" />
                  </div>

                  <div className="flex items-center justify-between rounded-lg border border-border/80 bg-muted/20 px-3 py-1.5">
                    <div className="min-w-0 pr-2">
                      <div className="text-xs font-medium text-foreground">Show Stock to Customers</div>
                      <div className="text-[10px] text-muted-foreground leading-tight">Display "Only N left" badge</div>
                    </div>
                    <Switch checked={!!form.show_stock} onCheckedChange={(v) => setForm({ ...form, show_stock: v })} data-testid="product-show-stock" />
                  </div>
                </div>
              </div>
            </section>

            {/* ── 4 · PACKAGING & NET QUANTITY ───────────────────────── */}
            <section className="rounded-xl border border-border/70 bg-card/60 p-3 space-y-2.5 shadow-sm">
              <div className="flex items-center gap-2 pb-1.5 border-b border-border/50">
                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center">4</span>
                <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">Packaging & Net Quantity</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <div>
                  <Label className="text-xs font-semibold text-foreground/85">Packaging Unit</Label>
                  <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="e.g. piece, pack, pouch, box" className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-foreground/85">Net Weight / Volume</Label>
                  <Input type="number" step="0.01" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} placeholder="e.g. 500, 1, 250" className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-foreground/85">Weight Unit</Label>
                  <Select value={form.weight_unit || "g"} onValueChange={(v) => setForm({ ...form, weight_unit: v })}>
                    <SelectTrigger className="mt-1 h-9 w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["g", "kg", "ml", "L", "pc", "pcs", "pack"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {/* ── 5 · MEDIA & DESCRIPTION ─────────────────────────────── */}
            <section className="rounded-xl border border-border/70 bg-card/60 p-3 space-y-2.5 shadow-sm">
              <div className="flex items-center gap-2 pb-1.5 border-b border-border/50">
                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center">5</span>
                <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">Media & Description</h3>
              </div>
              <ImageUpload
                value={form.thumbnail_url || ""}
                onChange={(v) => setForm({ ...form, thumbnail_url: v })}
                label="Product Image"
                hint="Upload a square image (PNG, JPG, WebP up to 3MB). Recommended size: 600×600px."
                compact
                testId="product-image"
              />
              <div>
                <Label className="text-xs font-semibold text-foreground/85">Description</Label>
                <Textarea rows={2.5} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short, honest description shown on the product details page." className="mt-1 resize-none" />
              </div>
            </section>

            {/* ── 6 · VISIBILITY & STATUS ─────────────────────────────── */}
            <section className="rounded-xl border border-border/70 bg-card/60 p-3 space-y-2.5 shadow-sm">
              <div className="flex items-center gap-2 pb-1.5 border-b border-border/50">
                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center">6</span>
                <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">Visibility & Status</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="flex items-center justify-between rounded-lg border border-border/80 bg-muted/20 px-3 py-2">
                  <div className="min-w-0 pr-2">
                    <div className="text-xs font-semibold text-foreground">Active Status</div>
                    <div className="text-[10px] text-muted-foreground leading-tight">Listed and available for sale</div>
                  </div>
                  <Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="product-active" />
                </div>

                <div className="flex items-center justify-between rounded-lg border border-border/80 bg-muted/20 px-3 py-2">
                  <div className="min-w-0 pr-2">
                    <div className="text-xs font-semibold text-foreground">Show Online</div>
                    <div className="text-[10px] text-muted-foreground leading-tight">Visible on customer app & website (POS always sees it)</div>
                  </div>
                  <Switch checked={form.is_online !== false} onCheckedChange={(v) => setForm({ ...form, is_online: v })} data-testid="product-online" />
                </div>
              </div>
            </section>

            {/* ── 7 · VARIANTS (OPTIONAL) ─────────────────────────────── */}
            {editing?.id && (
              <section className="rounded-xl border border-border/70 bg-card/60 p-3 space-y-2.5 shadow-sm">
                <div className="flex items-center gap-2 pb-1.5 border-b border-border/50">
                  <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center">7</span>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">Product Variants (Optional)</h3>
                </div>
                <VariantManager productId={editing.id} />
              </section>
            )}
          </div>

          <DialogFooter className="px-5 py-2.5 border-t bg-muted/20 shrink-0 flex flex-col sm:flex-row items-center justify-between gap-2">
            {submitted && (!form.name?.trim() || !form.selling_price || Number(form.selling_price) <= 0 || form.gst_rate === "" || form.gst_rate === null || Number(form.gst_rate) < 0) ? (
              <div className="text-xs font-medium text-destructive">
                * Please fill required fields: {!form.name?.trim() ? "Name" : !form.selling_price || Number(form.selling_price) <= 0 ? "Selling price" : "GST %"}
              </div>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button variant="outline" onClick={() => setOpen(false)} className="flex-1 sm:flex-none h-8.5">Cancel</Button>
              <Button
                onClick={save}
                disabled={saving}
                className="btn-primary-glow flex-1 sm:flex-none h-8.5"
                data-testid="save-product-btn"
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Create product"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
